"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shadcn-components/ui/tabs";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import { Badge } from "@/shadcn-components/ui/badge";
import { Switch } from "@/shadcn-components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcn-components/ui/alert-dialog";
import IndicatorAddScreen from "./IndicatorsAddScreen";
import IndicatorConfigScreen from "./IndicatorConfigScreen";
import CustomIndicatorsTab from "./custom/CustomIndicatorsTab";
import CustomIndicatorConfig from "./custom/CustomIndicatorConfig";
import { getCustomIndicator } from "@/lib/custom-indicators/db";
import { getCustomIndicatorIdFromInstanceId } from "@/lib/custom-indicators/indicator-source";
import { isCustomIndicatorId } from "@/lib/custom-indicators/no-op-template";
import type { CustomIndicatorRecord } from "@/lib/custom-indicators/types";
import { useChartIndicatorsStore } from "@/lib/chart-indicators";
import { useMainChartStore } from "@/lib/main-chart";
import {
  catalogEntryToInstance,
  formatParamSummary,
  type IndicatorInstance,
} from "@/lib/chart-indicators/registry";
import { formatCustomParamSummary } from "@/lib/custom-indicators/indicator-source";
import type { CatalogEntry } from "@/lib/chart-indicators/catalog";
import { useIsAdmin } from "@/lib/vybe-auth";

interface IndicatorsScreenProps {
  onBack: () => void;
  isDesktop?: boolean;
}

type ScreenView = "list" | "add" | "config" | "custom-config";

export default function IndicatorsScreen({
  onBack,
  isDesktop = false,
}: IndicatorsScreenProps) {
  const indicators = useChartIndicatorsStore((state) => state.indicators);
  const mainChart = useMainChartStore((state) => state.mainChart);
  const addWindow = useChartIndicatorsStore((state) => state.addWindow);
  const addMainIndicator = useMainChartStore((state) => state.addIndicator);
  const deleteMainIndicator = useMainChartStore(
    (state) => state.deleteIndicator
  );
  const deleteWindow = useChartIndicatorsStore((state) => state.deleteWindow);

  const [view, setView] = useState<ScreenView>("list");
  const [activeTab, setActiveTab] = useState<"active" | "add" | "custom">(
    "active"
  );
  // Custom indicators are an admin-only surface (client-side UX gating only).
  const isAdmin = useIsAdmin();
  useEffect(() => {
    if (!isAdmin && activeTab === "custom") setActiveTab("active");
  }, [isAdmin, activeTab]);
  const [configuringIndicator, setConfiguringIndicator] =
    useState<IndicatorInstance | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [customConfigRecord, setCustomConfigRecord] =
    useState<CustomIndicatorRecord | null>(null);
  const [customConfigInstance, setCustomConfigInstance] =
    useState<IndicatorInstance | null>(null);

  const mainIndicators: IndicatorInstance[] = useMemo(
    () => (mainChart.indicators ?? []) as IndicatorInstance[],
    [mainChart.indicators]
  );
  const separateIndicators: IndicatorInstance[] = useMemo(
    () => (indicators.chartIndicators ?? []) as IndicatorInstance[],
    [indicators.chartIndicators]
  );

  const onAddIndicator = useCallback(
    (indicator: IndicatorInstance) => {
      if (indicator.createsNewChart) {
        addWindow(indicator as Parameters<typeof addWindow>[0]);
      } else {
        addMainIndicator(indicator);
      }
    },
    [addWindow, addMainIndicator]
  );

  const onUpdateIndicator = useCallback(
    (indicator: IndicatorInstance) => {
      if (indicator.createsNewChart) {
        const updated = separateIndicators.map((i) =>
          i.id === indicator.id ? { ...i, ...indicator } : i
        );
        useChartIndicatorsStore
          .getState()
          .updateIndicator("chartIndicators", updated);
      } else {
        const updated = mainIndicators.map((i) =>
          i.id === indicator.id ? { ...i, ...indicator } : i
        );
        useMainChartStore.getState().updateMainChart("indicators", updated);
      }
    },
    [mainIndicators, separateIndicators]
  );

  const setMainIndicatorVisible = useCallback(
    (id: string, visible: boolean) => {
      const updated = mainIndicators.map((i) =>
        i.id === id ? { ...i, visible } : i
      );
      useMainChartStore.getState().updateMainChart("indicators", updated);
    },
    [mainIndicators]
  );

  const setSeparateIndicatorVisible = useCallback(
    (id: string, visible: boolean) => {
      const updated = separateIndicators.map((i) =>
        i.id === id ? { ...i, visible } : i
      );
      useChartIndicatorsStore
        .getState()
        .updateIndicator("chartIndicators", updated);
    },
    [separateIndicators]
  );

  const handleSelectCatalogEntry = (entry: CatalogEntry) => {
    const instance = catalogEntryToInstance(entry.indicatorId);
    setConfiguringIndicator(instance);
    setIsEditMode(false);
    setView("config");
  };

  const handleEditIndicator = async (indicator: IndicatorInstance) => {
    if (isCustomIndicatorId(indicator.indicatorId)) {
      const customId = getCustomIndicatorIdFromInstanceId(
        indicator.indicatorId
      );
      if (customId) {
        const record = await getCustomIndicator(customId);
        if (record) {
          setCustomConfigRecord(record);
          setCustomConfigInstance({ ...indicator });
          setView("custom-config");
          return;
        }
      }
    }
    setConfiguringIndicator({ ...indicator });
    setIsEditMode(true);
    setView("config");
  };

  const handleAddCustomToChart = async (record: CustomIndicatorRecord) => {
    const { ensureCompiledFromBuild } = await import(
      "@/lib/custom-indicators/hydrate-build"
    );
    const loaded = await ensureCompiledFromBuild(record.id);
    if (!loaded?.success) {
      return;
    }
    setCustomConfigRecord(record);
    setCustomConfigInstance(null);
    setView("custom-config");
  };

  const handleApplyCustomConfig = (indicator: IndicatorInstance) => {
    if (customConfigInstance) {
      onUpdateIndicator(indicator);
    } else {
      onAddIndicator(indicator);
    }
    setCustomConfigRecord(null);
    setCustomConfigInstance(null);
    setView("list");
    setActiveTab("active");
  };

  const handleApplyConfig = (indicator: unknown) => {
    const inst = indicator as IndicatorInstance;
    if (isEditMode) {
      onUpdateIndicator(inst);
    } else {
      onAddIndicator(inst);
    }
    setConfiguringIndicator(null);
    setIsEditMode(false);
    setView("list");
    setActiveTab("active");
  };

  if (view === "custom-config" && customConfigRecord) {
    return (
      <CustomIndicatorConfig
        record={customConfigRecord}
        existingInstance={customConfigInstance}
        onBack={() => {
          setCustomConfigRecord(null);
          setCustomConfigInstance(null);
          setView("list");
        }}
        onApply={handleApplyCustomConfig}
      />
    );
  }

  if (view === "config" && configuringIndicator) {
    return (
      <IndicatorConfigScreen
        indicator={
          configuringIndicator as unknown as Record<string, unknown> & {
            indicatorId: string;
            name: string;
            createsNewChart?: boolean;
            settings?: { values?: Record<string, unknown> };
          }
        }
        onBack={() => {
          setConfiguringIndicator(null);
          setIsEditMode(false);
          setView("list");
        }}
        onApply={handleApplyConfig}
        isEditing={isEditMode}
      />
    );
  }

  if (view === "add") {
    return (
      <IndicatorAddScreen
        onBack={() => setView("list")}
        onSelectIndicator={handleSelectCatalogEntry}
        isDesktop={isDesktop}
      />
    );
  }

  const headerH = isDesktop ? "h-10 px-3" : "h-14 px-4";

  return (
    <div className="h-full flex flex-col min-h-0 bg-background text-foreground">
      <div
        className={`flex items-center justify-between border-b border-border shrink-0 ${headerH}`}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className={isDesktop ? "h-7 w-7" : "h-10 w-10"}
        >
          <ChevronLeft className={isDesktop ? "h-4 w-4" : "h-5 w-5"} />
        </Button>
        <h1 className={`font-medium ${isDesktop ? "text-sm" : "text-lg"}`}>
          Indicators
        </h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className={`text-primary ${isDesktop ? "text-xs h-7" : ""}`}
        >
          Done
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setActiveTab(v as "active" | "add" | "custom")
        }
        className="flex flex-col flex-1 min-h-0"
      >
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent h-9 px-2 shrink-0">
          <TabsTrigger value="active" className="text-xs data-[state=active]:bg-muted">
            Active
          </TabsTrigger>
          <TabsTrigger value="add" className="text-xs data-[state=active]:bg-muted">
            Add
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger
              value="custom"
              className="text-xs data-[state=active]:bg-muted"
            >
              Custom
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active" className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden">
          <ScrollArea className="h-full">
            <div className="p-3 space-y-4">
              <IndicatorGroup
                title="Main chart"
                emptyLabel="No overlays on main chart"
                indicators={mainIndicators}
                onEdit={handleEditIndicator}
                onDelete={deleteMainIndicator}
                onToggleVisible={(id, v) => setMainIndicatorVisible(id, v)}
                isDesktop={isDesktop}
              />
              <IndicatorGroup
                title="Separate panes"
                emptyLabel="No indicator panes"
                indicators={separateIndicators}
                onEdit={handleEditIndicator}
                onDelete={deleteWindow}
                onToggleVisible={(id, v) => setSeparateIndicatorVisible(id, v)}
                isDesktop={isDesktop}
              />
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="add" className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden">
          <IndicatorAddScreen
            onBack={() => setActiveTab("active")}
            onSelectIndicator={handleSelectCatalogEntry}
            isDesktop={isDesktop}
          />
        </TabsContent>

        {isAdmin && (
          <TabsContent
            value="custom"
            className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden flex flex-col"
          >
            <CustomIndicatorsTab
              isDesktop={isDesktop}
              onAddToChart={handleAddCustomToChart}
            />
          </TabsContent>
        )}
      </Tabs>

      {activeTab === "active" && (
        <div className="p-3 border-t border-border shrink-0">
          <Button
            className="w-full gap-2"
            size="sm"
            onClick={() => setActiveTab("add")}
          >
            <Plus className="h-4 w-4" />
            Add indicator
          </Button>
        </div>
      )}
    </div>
  );
}

function IndicatorGroup({
  title,
  emptyLabel,
  indicators,
  onEdit,
  onDelete,
  onToggleVisible,
  isDesktop,
}: {
  title: string;
  emptyLabel: string;
  indicators: IndicatorInstance[];
  onEdit: (i: IndicatorInstance) => void;
  onDelete: (id: string) => void;
  onToggleVisible: (id: string, visible: boolean) => void;
  isDesktop?: boolean;
}) {
  return (
    <section>
      <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
        {title}
      </h2>
      {indicators.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center rounded-lg border border-dashed border-border">
          {emptyLabel}
        </p>
      ) : (
        <ul className="space-y-2">
          {indicators.map((indicator) => (
            <IndicatorRow
              key={indicator.id}
              indicator={indicator}
              onEdit={() => onEdit(indicator)}
              onDelete={() => onDelete(indicator.id)}
              onToggleVisible={(v) => onToggleVisible(indicator.id, v)}
              isDesktop={isDesktop}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function IndicatorRow({
  indicator,
  onEdit,
  onDelete,
  onToggleVisible,
}: {
  indicator: IndicatorInstance;
  onEdit: () => void;
  onDelete: () => void;
  onToggleVisible: (visible: boolean) => void;
  isDesktop?: boolean;
}) {
  const summary = isCustomIndicatorId(indicator.indicatorId)
    ? formatCustomParamSummary(indicator.settings?.values)
    : formatParamSummary(indicator);
  const visible = indicator.visible !== false;

  return (
    <li className="flex items-center gap-2 rounded-lg border border-border bg-card/40 px-3 py-2">
      <Switch
        checked={visible}
        onCheckedChange={onToggleVisible}
        aria-label={visible ? "Hide indicator" : "Show indicator"}
        className="shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{indicator.name}</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {isCustomIndicatorId(indicator.indicatorId)
              ? "Custom"
              : indicator.createsNewChart
                ? "Pane"
                : "Overlay"}
          </Badge>
        </div>
        {summary ? (
          <p className="text-xs text-muted-foreground truncate">{summary}</p>
        ) : null}
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onEdit}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove indicator</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &quot;{indicator.name}&quot; from the chart?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

